// 日付・時刻のフォーマット操作ライブラリ（例：YYYY-MM-DD HH:mm:ss に変換）
import moment from "moment";

// API通信ライブラリ（PromiseベースのHTTPクライアント）
import axios from "axios";

// APIのルートパス定義を集約した定数ファイル
import ApiRouterConstants from "../constants/api.router.constants";

// VueUseが提供するユーティリティフック。useStorageでsessionStorageにstateを保持
import { useStorage } from "@vueuse/core";

/**
 * 悩みの情報をproblemsカラム用の形式に変換する
 *
 * @param selectedWorryTags - 選択された悩みタグのID配列
 * @param worryContents - 悩みコンテンツの配列
 * @returns problemsオブジェクト（例: {"1": ["薄毛", "白髪"], "2": ["犬", "ハムスター"]}）
 */
const buildProblemsData = (selectedWorryTags: number[], worryContents: any[]) => {
  const problems: { [key: string]: string[] } = {};

  worryContents.forEach(content => {
    const selectedTags = content.worry_tags
      .filter((tag: any) => selectedWorryTags.includes(tag.id))
      .map((tag: any) => tag.name);

    if (selectedTags.length > 0) {
      problems[content.id.toString()] = selectedTags;
    }
  });

  return problems;
};

/**
 * 通常予約のパラメータを構築
 *
 * @param state - Vuex state
 * @returns 通常予約用のcustomerパラメータ
 */
const buildNormalBookingParams = (state: any) => {
  return {
    customer_name: `${state.customerInfo.lastName} ${state.customerInfo.firstName}`,
    customer_email: state.customerInfo.email,
    customer_phone: state.customerInfo.tel,
    customer_birthday: state.customerInfo.birthday,
    customer_address: state.customerInfo.address,
    customer_address_number: state.customerInfo.addressNumber,
    customer_zipcode: state.customerInfo.zipcode,
    customer_gender: state.customerInfo.gender,
    problems: buildProblemsData(state.customerInfo.selectedWorryTags || [], state.worryContents || []),
  };
};

/**
 * 子ども予約のパラメータを構築
 *
 * @param state - Vuex state
 * @returns 子ども予約用のcustomer + child_customerパラメータ
 */
const buildChildBookingParams = (state: any) => {
  const params: any = {
    // 子ども情報（結合形式）
    child_customer_name: `${state.childInfo.lastName} ${state.childInfo.firstName}`,
    child_customer_name_kana: `${state.childInfo.lastNameKana} ${state.childInfo.firstNameKana}`,
    child_customer_birthday: state.childInfo.birthday,
    child_customer_gender: state.childInfo.gender,

    // 親情報（customer_*、結合形式）
    customer_name: `${state.parentInfo.lastName} ${state.parentInfo.firstName}`,
    customer_email: state.parentInfo.email,
    customer_phone: state.parentInfo.tel,
    customer_birthday: state.parentInfo.birthday,
    customer_gender: state.parentInfo.gender,
    customer_zipcode: state.parentInfo.zipcode,
    customer_address: state.parentInfo.address,
    customer_address_number: state.parentInfo.addressNumber,
  };

  // 既存の子どもを選択した場合はIDを追加
  if (state.selectedChildId) {
    params.child_customer_id = state.selectedChildId;
  }

  return params;
};

/**
 * Bookingモジュール
 * ユーザーの予約情報（氏名、連絡先、メニュー、スタッフ、部屋、時間帯など）をVuexで管理する
 * 状態は sessionStorage を通じてセッション間でも永続化される
 */
const defaultState = () => {
  return {
    /** 予約ID（0のときは新規予約） */
    bookingId: useStorage("bookingId", 0, sessionStorage),

    /** 顧客ID（予約編集時の顧客特定用） */
    customerId: useStorage("customerId", 0, sessionStorage),

    /** 選択されたスタッフのID */
    selectedStaffId: useStorage("selectedStaffId", undefined, sessionStorage),

    /** デフォルトスタッフ使用フラグ */
    useDefaultStaff: useStorage("useDefaultStaff", false, sessionStorage),

    /** 自動スタッフ振り分け関連情報 */
    autoStaffDistribution: {
      data: useStorage("autoStaffDistribution::data", [], sessionStorage),
      selectedStaffId: useStorage("autoStaffDistribution::selectedStaffId", "", sessionStorage),
    },

    /** 自動で振り分けられたスタッフID */
    distributedStaffid: useStorage("distributedStaffid", undefined, sessionStorage),

    /** 選択されたメニューカテゴリID */
    selectedMenuCategoryId: useStorage("selectedMenuCategoryId", 0, sessionStorage),

    /** 選択されたメニュー情報 */
    selectedMenu: {
      id: useStorage("selectedMenu::id", 0, sessionStorage),
      price: useStorage("selectedMenu::price", 0, sessionStorage),
      time: useStorage("selectedMenu::time", 0, sessionStorage),
      name: useStorage("selectedMenu::name", "", sessionStorage),
      displayPrice: useStorage("selectedMenu::displayPrice", false, sessionStorage),
      bookingType: useStorage("selectedMenu::bookingType", "normal", sessionStorage),
      staff_ids: useStorage("selectedMenu::staff_ids", [], sessionStorage),
    },

    /** 選択された部屋ID */
    selectedRoomId: useStorage("selectedRoomId", 0, sessionStorage),

    /** 選択されたオプション一覧 */
    selectedOptions: useStorage("selectedOptions", [], sessionStorage),

    /** 希望日時（第1～第3） */
    selectedRequestDates: {
      first: useStorage("selectedRequestDates::first", "", sessionStorage),
      second: useStorage("selectedRequestDates::second", "", sessionStorage),
      third: useStorage("selectedRequestDates::third", "", sessionStorage),
    },

    /** 各希望日時に対する部屋の指定 */
    selectedRoomForRequests: {
      first: useStorage("selectedRoomForRequests::first", 0, sessionStorage),
      second: useStorage("selectedRoomForRequests::second", 0, sessionStorage),
      third: useStorage("selectedRoomForRequests::third", 0, sessionStorage),
    },

    /** 実際に選択された日時（date & time） */
    selectedTime: useStorage("selectedTime", {}, sessionStorage),

    /**
     * 顧客情報
     * - lastName: 姓
     * - firstName: 名
     */
    customerInfo: {
      email: useStorage("customerInfo::email", "", sessionStorage),
      firstName: useStorage("customerInfo::firstName", "", sessionStorage),
      lastName: useStorage("customerInfo::lastName", "", sessionStorage),
      tel: useStorage("customerInfo::tel", "", sessionStorage),
      zipcode: useStorage("customerInfo::zipcode", "", sessionStorage),
      address: useStorage("customerInfo::address", "", sessionStorage),
      addressNumber: useStorage("customerInfo::addressNumber", "", sessionStorage),
      birthday: useStorage("customerInfo::birthday", "", sessionStorage),
      gender: useStorage("customerInfo::gender", "male", sessionStorage),
      note: useStorage("customerInfo::note", "", sessionStorage),
      noteSymptom: useStorage("customerInfo::noteSymptom", "", sessionStorage),
      selectedWorryTags: useStorage("customerInfo::selectedWorryTags", [], sessionStorage),
    },

    /** 保護者の情報 */
    parentInfo: {
      firstName: useStorage("parentInfo::firstName", "", sessionStorage),
      lastName: useStorage("parentInfo::lastName", "", sessionStorage),
      firstNameKana: useStorage("parentInfo::firstNameKana", "", sessionStorage),
      lastNameKana: useStorage("parentInfo::lastNameKana", "", sessionStorage),
      gender: useStorage("parentInfo::gender", "male", sessionStorage),
      birthday: useStorage("parentInfo::birthday", "", sessionStorage),
      tel: useStorage("parentInfo::tel", "", sessionStorage),
      email: useStorage("parentInfo::email", "", sessionStorage),
      zipcode: useStorage("parentInfo::zipcode", "", sessionStorage),
      address: useStorage("parentInfo::address", "", sessionStorage),
      addressNumber: useStorage("parentInfo::addressNumber", "", sessionStorage),
    },

    /** お子様の情報 */
    childInfo: {
      firstName: useStorage("childInfo::firstName", "", sessionStorage),
      lastName: useStorage("childInfo::lastName", "", sessionStorage),
      firstNameKana: useStorage("childInfo::firstNameKana", "", sessionStorage),
      lastNameKana: useStorage("childInfo::lastNameKana", "", sessionStorage),
      gender: useStorage("childInfo::gender", "male", sessionStorage),
      birthday: useStorage("childInfo::birthday", "", sessionStorage),
      note: useStorage("childInfo::note", "", sessionStorage),
      noteSymptom: useStorage("childInfo::noteSymptom", "", sessionStorage),
    },

    /** 子ども予約フラグ */
    isChildBooking: useStorage("isChildBooking", false, sessionStorage),

    /** 選択された子どもID（既存の子ども選択時に使用） */
    selectedChildId: useStorage("selectedChildId", null, sessionStorage),

    /** LINE通知のためのメッセージトークン */
    messageToken: useStorage("booking::messageToken", "", sessionStorage),
    messageType: useStorage("booking::messageType", "", sessionStorage),

    /** 上部表示用の予約詳細 */
    headerBookingDetails: useStorage("booking:headerBookingDetails", "", sessionStorage),

    /** 通知用の予約詳細・リクエスト */
    notificationsBookingDetails: useStorage("booking:BookingNotifyBookingDetails", "", sessionStorage),
    notificationsBookingRequest: useStorage("booking:BookingNotifyBookingDetails", "", sessionStorage),

    /** ワンタイムパスコード（認証用） */
    otpCode: undefined,

    /** 予約完了済みフラグ */
    submited: false,

    /** 合計金額 */
    totalPrice: 0,

    /** 編集フラグ */
    edit: false,

    /** 選択されたスタッフ情報 */
    selectedStaff: useStorage("selectedStaff", {}, sessionStorage),

    /** 悩みコンテンツリスト */
    worryContents: useStorage("worryContents", [], sessionStorage),

    /** 自由予約フラグ（LIFFからは常にfalse=通常予約のみ） */
    isFreeSpace: false,
  };
};

/**
 * 予約管理用 Vuex モジュール
 *
 * - 顧客の予約に関する状態（スタッフ・メニュー・時間・顧客情報など）を保持
 * - 非同期処理（予約送信）や状態更新処理（ミューテーション）を提供
 * - 名前空間（namespaced: true）で登録され、他モジュールと衝突しないように設計
 *
 * 使用例:
 *   store.registerModule('booking', Booking)
 *   store.dispatch('booking/submitBooking', callbacks)
 */
const Booking = {
  /**
   * モジュール名を名前空間付きで登録
   * - 他のモジュールとミューテーション・アクションが衝突しないようにする
   * - 例: dispatch('booking/submitBooking') のように呼び出し可能
   */
  namespaced: true,

  /**
   * 初期stateを返す関数
   * - 顧客の予約情報・選択状態などを保持
   * - useStorage により sessionStorage を通じて永続化される
   */
  state: defaultState,

  /**
   * Vuex mutations 定義
   * - stateを同期的に更新する処理群
   * - 主に画面入力や選択に応じて、Vuex stateの各フィールドを直接書き換える
   */
  mutations: {
    /**
     * OTPコードを保存
     *
     * @param state - 現在のstate
     * @param otpCode - 入力されたOTPコード
     */
    setOtpCode: (state: any, otpCode: number) => {
      state.otpCode = otpCode;
    },

    /**
     * 編集モード（edit）フラグを切り替える
     *
     * @param state
     * @param edit - 編集モードかどうか
     */
    setEditFlag: (state: any, edit: boolean) => {
      state.edit = edit;
    },

    /**
     * デフォルトスタッフの使用有無を設定
     *
     * @param state
     * @param enable - trueならデフォルトスタッフを使用
     */
    setUseDefaultStaff: (state: any, enable: boolean) => {
      state.useDefaultStaff = enable;
    },

    /**
     * 自由予約フラグを設定
     *
     * @param state
     * @param isFreeSpace - 自由予約フラグ（LIFF側では常にfalse）
     */
    setIsFreeSpace: (state: any, isFreeSpace: boolean) => {
      state.isFreeSpace = isFreeSpace;
    },

    /**
     * スタッフ情報を選択状態に設定
     *
     * @param state
     * @param staff - スタッフオブジェクト or スタッフID
     */
    selectStaff: (state: any, staff: any) => {
      state.selectedStaffId = staff.id || staff;
      state.selectedStaff = staff;
    },

    /**
     * メニューカテゴリを選択
     *
     * @param state
     * @param id - メニューカテゴリID
     */
    selectMenuCategory: (state: any, id: number) => {
      state.selectedMenuCategoryId = id;
    },

    /**
     * メニュー詳細を選択
     *
     * @param state
     * @param menu - メニューオブジェクト
     */
    selectMenu: (state: any, menu: any) => {
      state.selectedMenu.id = menu.id;
      state.selectedMenu.price = menu.unit_price;
      state.selectedMenu.time = menu.time_unit;
      state.selectedMenu.name = menu.name;
      state.selectedMenu.displayPrice = menu.display_price;
      state.selectedMenu.bookingType = menu.booking_type;
      state.selectedMenu.staff_ids = menu.staff_ids;
    },

    /**
     * 使用する部屋IDを設定
     *
     * @param state
     * @param id - 部屋ID
     */
    selectRoom: (state: any, id: number) => {
      state.selectedRoomId = id;
    },

    /**
     * メニューオプションを選択（複数）
     *
     * @param state
     * @param options - オプション一覧
     */
    selectOptions: (state: any, options: any[]) => {
      const menuOptions = options.map(option => {
        return {
          id: option.id,
          price: option.unit_price,
          time: option.time_unit,
          name: option.name,
          displayPrice: option.display_price,
        };
      });
      state.selectedOptions = menuOptions;
    },

    /**
     * 選択された日時を設定
     *
     * @param state
     * @param time - 日付 + 時刻情報
     */
    selectTime: (state: any, time: any) => {
      const date = moment(time);
      state.selectedTime = {
        date: date.format("YYYY-MM-DD"),
        time: date.format("HH:mm"),
      };
    },

    /**
     * ヘッダー用の予約詳細データを保存
     *
     * @param state
     * @param details
     */
    setHeaderBookingDetails: (state: any, details: any) => {
      state.headerBookingDetails = details;
    },

    /**
     * 通知表示用の予約詳細を保存
     *
     * @param state
     * @param details
     */
    setNotificationsBookingDetails: (state: any, details: any) => {
      state.notificationsBookingDetails = details;
    },

    /**
     * 通知表示用の予約リクエスト詳細を保存
     *
     * @param state
     * @param request
     */
    setNotificationsBookingRequests: (state: any, request: any) => {
      state.notificationsBookingRequests = request;
    },

    /**
     * 顧客情報を保存（正しい項目へマッピング）
     *
     * @param state
     * @param info - 顧客情報（姓・名・電話・住所など）
     */
    setCustomerInfo: (state: any, info: any) => {
      state.customerInfo.email = info.email;
      state.customerInfo.firstName = info.firstName;
      state.customerInfo.lastName = info.lastName;
      state.customerInfo.tel = info.tel;
      state.customerInfo.zipcode = info.zipcode;
      state.customerInfo.address = info.address;
      state.customerInfo.addressNumber = info.addressNumber;
      state.customerInfo.birthday = info.birthday;
      state.customerInfo.gender = info.gender;
      state.customerInfo.note = info.note;
      state.customerInfo.noteSymptom = info.noteSymptom;
      state.customerInfo.selectedWorryTags = info.selectedWorryTags;
    },

    /**
     * 保護者の情報を保存
     *
     * @param state
     * @param info - 保護者情報
     */
    setParentInfo: (state: any, info: any) => {
      state.parentInfo.firstName = info.firstName;
      state.parentInfo.lastName = info.lastName;
      state.parentInfo.firstNameKana = info.firstNameKana;
      state.parentInfo.lastNameKana = info.lastNameKana;
      state.parentInfo.gender = info.gender;
      state.parentInfo.birthday = info.birthday;
      state.parentInfo.tel = info.tel;
      state.parentInfo.email = info.email;
      state.parentInfo.zipcode = info.zipcode;
      state.parentInfo.address = info.address;
      state.parentInfo.addressNumber = info.addressNumber;
    },

    /**
     * お子様の情報を保存
     *
     * @param state
     * @param info - お子様情報
     */
    setChildInfo: (state: any, info: any) => {
      state.childInfo.firstName = info.firstName;
      state.childInfo.lastName = info.lastName;
      state.childInfo.firstNameKana = info.firstNameKana;
      state.childInfo.lastNameKana = info.lastNameKana;
      state.childInfo.gender = info.gender;
      state.childInfo.birthday = info.birthday;
      state.childInfo.note = info.note;
      state.childInfo.noteSymptom = info.noteSymptom;
    },

    /**
     * 子ども予約フラグを設定
     *
     * @param state
     * @param isChildBooking - 子ども予約フラグ
     */
    setIsChildBooking: (state: any, isChildBooking: boolean) => {
      state.isChildBooking = isChildBooking;
    },

    /**
     * 選択された子どもIDを設定
     *
     * @param state
     * @param id - 子どもID（既存の子ども選択時）
     */
    setSelectedChildId: (state: any, id: number | null) => {
      state.selectedChildId = id;
    },

    /**
     * 悩みコンテンツリストを保存
     *
     * @param state
     * @param worryContents - 悩みコンテンツの配列
     */
    setWorryContents: (state: any, worryContents: any[]) => {
      state.worryContents = worryContents;
    },

    /**
     * LINEメッセージ送信に必要なトークン情報を保存
     *
     * @param state
     * @param message - { token, type }
     */
    setMessage: (state: any, message: any) => {
      state.messageToken = message.token;
      state.messageType = message.type;
    },

    /**
     * Bookingモジュールの状態全体を初期化（セッションもクリア）
     *
     * @param state
     */
    resetState(state: any) {
      sessionStorage.clear();
      Object.assign(state, defaultState());
    },

    /**
     * 選択された時間情報のみ初期化
     *
     * @param state
     */
    resetSelectedTime(state: any) {
      state.selectedTime = {};
    },

    /**
     * 編集モード時のパラメータを初期設定
     *
     * @param state
     * @param booking - 編集対象の予約データ
     */
    setBookingParams(state: any, booking: any) {
      state.bookingId = booking.id;
      state.customerId = booking.customer_id || 0;
      state.customerInfo.note = booking.note;
      state.customerInfo.noteSymptom = booking.note_symptom || "";
      state.selectedStaffId = undefined;
      state.selectedMenu.id = booking.medical_menu_id;
      state.selectedMenu.time = booking.medical_menu?.time_unit;
      state.selectedOptions = booking.medical_options.map((option: any) => {
        return { id: option.id, time: option.time_unit };
      });
    },

    /**
     * 希望日時（第1〜3）を設定
     *
     * @param state
     * @param dates - { first, second, third }
     */
    setRequestDates(state: any, dates: any) {
      state.selectedRequestDates.first = dates.first;
      state.selectedRequestDates.second = dates.second;
      state.selectedRequestDates.third = dates.third;
    },

    /**
     * 希望日時ごとの部屋指定を設定
     *
     * @param state
     * @param rooms - { first, second, third }
     */
    setRoomIdForRequests(state: any, rooms: any) {
      state.selectedRoomForRequests.first = rooms.first;
      state.selectedRoomForRequests.second = rooms.second;
      state.selectedRoomForRequests.third = rooms.third;
    },

    /**
     * 自動スタッフ振り分け候補のリストを保存
     *
     * @param state
     * @param data - スタッフIDの配列
     */
    setAutoStaffDistributionData: (state: any, data: number[]) => {
      state.autoStaffDistribution.data = data;
    },

    /**
     * 自動的に振り分けられたスタッフIDを保存
     *
     * @param state
     * @param staffId
     */
    selectDistributedStaff: (state: any, staffId: any) => {
      state.autoStaffDistribution.selectedStaffId = staffId;
    },
  },

  /**
   * Vuexアクション定義
   * - 非同期処理やAPI通信などの副作用を含むロジックを記述する
   * - stateの直接更新はせず、必要に応じてmutationsを呼び出す
   */
  actions: {
    /**
     * 予約データを Recoma API に送信するアクション
     *
     * - 新規予約の場合は POST、編集時は PATCH を使用
     * - 顧客情報（姓・名含む）やメニュー、スタッフ、日時、メモなどを送信
     * - 氏名は `${lastName} ${firstName}` の順で送信する
     *
     * @param {Object} context - Vuex context
     * @param {Object} context.state - 現在のstate
     * @param {Object} context.getters - 現在のgetters
     * @param {Object} context.rootState - ルートのstate（例：line_account など）
     * @param {Object} callbacks - 成功・失敗時のコールバック関数
     * @param {Function} callbacks.success - 成功時に呼ばれる関数
     * @param {Function} callbacks.error - エラー時に呼ばれる関数
     */
    submitBooking({ state, getters, rootState }: any, callbacks: any) {
      let method = "POST";
      let url = ApiRouterConstants.API_BOOKING;

      // 編集（更新）の場合はPATCHに切り替え
      if (state.bookingId != 0) {
        method = "PATCH";
        url = `${url}/${state.bookingId}`;
      }

      // 通常予約 or 子ども予約でパラメータを分岐
      const customerParams = state.isChildBooking ? buildChildBookingParams(state) : buildNormalBookingParams(state);

      const options = {
        method: method,
        url: url,
        params: {
          // クリニック情報・スタッフ情報
          clinic_id: rootState.line_account.clinic_id,
          staff_id: state.selectedStaffId,
          distributed_staff_id: rootState.bookingSetting.autoStaffDistribution
            ? state.autoStaffDistribution.selectedStaffId
            : undefined,
          customer_id: rootState.line_account.customerId,

          // メニュー・部屋・カテゴリ
          medical_menu_category_id: state.selectedMenuCategoryId == 0 ? null : state.selectedMenuCategoryId,
          medical_menu_id: state.selectedMenu.id,
          room_id: state.selectedRoomId == 0 ? null : state.selectedRoomId,

          // 顧客識別
          line_account_id: rootState.line_account.id,

          // 顧客情報（通常予約 or 子ども予約で分岐）
          ...customerParams,

          // メッセージ通知情報
          message_token: state.messageToken,
          message_type: state.messageType,

          // メモ情報
          note: state.customerInfo.note,
          note_symptom: state.isChildBooking ? state.childInfo.noteSymptom : state.customerInfo.noteSymptom,

          // 予約種別（通常 or リクエスト）
          booking_type: state.selectedMenu.bookingType,

          // 自由予約フラグ（LIFFからは常にfalse=通常予約のみ）
          is_free_space: state.isFreeSpace ?? false,

          otp_code: state.otpCode,

          // 第1希望
          request_date_first:
            state.selectedRequestDates.first == null
              ? ""
              : moment(state.selectedRequestDates.first).format("YYYY-MM-DD HH:mm:ss"),
          room_id_request_first: state.selectedRoomForRequests.first == 0 ? null : state.selectedRoomForRequests.first,

          // 第2希望
          request_date_second:
            state.selectedRequestDates.second == null
              ? ""
              : moment(state.selectedRequestDates.second).format("YYYY-MM-DD HH:mm:ss"),
          room_id_request_second:
            state.selectedRoomForRequests.second == 0 ? null : state.selectedRoomForRequests.second,

          // 第3希望
          request_date_third:
            state.selectedRequestDates.third == null
              ? ""
              : moment(state.selectedRequestDates.third).format("YYYY-MM-DD HH:mm:ss"),
          room_id_request_third: state.selectedRoomForRequests.third == 0 ? null : state.selectedRoomForRequests.third,

          // オプションID一覧
          medical_option_ids: state.selectedOptions.map((option: any) => option.id),

          // 実予約の開始・終了時間（合計時間で調整）
          start_time:
            state.selectedTime.date == null
              ? ""
              : moment(`${state.selectedTime.date} ${state.selectedTime.time}`).format("YYYY-MM-DD HH:mm:ss"),
          end_time:
            state.selectedTime.date == null
              ? ""
              : moment(`${state.selectedTime.date} ${state.selectedTime.time}`)
                  .add(getters.totalTime, "minutes")
                  .format("YYYY-MM-DD HH:mm:ss"),
        },
        headers: { Accept: "application/json" },
      };

      // API送信処理実行
      axios
        .request(options)
        .then(response => {
          state.totalPrice = response.data.final_amount;
          state.submited = true;
          callbacks.success();
        })
        .catch(response => {
          callbacks.error(response);
        });
    },
  },

  /**
   * Vuex getters 定義
   *
   * - stateに基づく派生データ・UI用の整形データを提供
   * - 他コンポーネントやアクション・ミューテーションから状態を安全に取得可能
   */
  getters: {
    /**
     * 編集モードかどうかを返す
     *
     * @param {Object} state - Vuex state
     * @returns {boolean}
     */
    editFlag: (state: any) => {
      return state.edit;
    },

    /**
     * 予約IDを返す
     *
     * - 新規作成時は 0
     * - 予約変更時は対象予約の ID
     *
     * @param {Object} state - Vuex state
     * @returns {number} 予約ID
     */
    bookingId: (state: any): number => {
      return state.bookingId;
    },

    /**
     * 顧客IDを取得（予約編集時の顧客特定用）
     *
     * @param {Object} state - Vuex state
     * @returns {number} 顧客ID
     */
    customerId: (state: any): number => {
      return state.customerId;
    },

    /**
     * 希望日（第1〜第3希望）を取得
     *
     * @param {Object} state - Vuex state
     * @returns {{ first: string, second: string, third: string }}
     */
    selectedRequestDates: (state: any) => {
      return state.selectedRequestDates;
    },

    /**
     * 選択されたスタッフのIDを取得
     *
     * @param {Object} state - Vuex state
     * @returns {number | undefined}
     */
    selectedStaffId: (state: any) => {
      return state.selectedStaffId;
    },

    /**
     * デフォルトスタッフ使用の有無を返す
     *
     * @param {Object} state - Vuex state
     * @returns {boolean}
     */
    selectUseDefaultStaff: (state: any) => {
      return state.useDefaultStaff;
    },

    /**
     * 選択されているメニューカテゴリIDを返す
     *
     * @param {Object} state - Vuex state
     * @returns {number}
     */
    selectedMenuCategoryId: (state: any) => {
      return state.selectedMenuCategoryId;
    },

    /**
     * 選択されているメニュー情報を返す
     *
     * @param {Object} state - Vuex state
     * @returns {Object}
     */
    selectedMenu: (state: any) => {
      return state.selectedMenu;
    },

    /**
     * 選択されたオプションの一覧を返す
     *
     * @param {Object} state - Vuex state
     * @returns {Array}
     */
    selectedOptions: (state: any) => {
      return state.selectedOptions;
    },

    /**
     * 選択された予約日付と時間を返す
     *
     * @param {Object} state - Vuex state
     * @returns {{ date: string, time: string }}
     */
    selectedTime: (state: any) => {
      return state.selectedTime;
    },

    /**
     * 上部に表示される予約詳細を返す
     *
     * @param {Object} state - Vuex state
     * @returns {string}
     */
    headerBookingDetails: (state: any) => {
      return state.headerBookingDetails;
    },

    /**
     * 通知表示用の予約詳細データを返す
     *
     * @param {Object} state - Vuex state
     * @returns {string}
     */
    notificationsBookingDetails: (state: any) => {
      return state.notificationsBookingDetails;
    },

    /**
     * 通知表示用の予約リクエストデータを返す
     *
     * @param {Object} state - Vuex state
     * @returns {string}
     */
    notificationsBookingRequest: (state: any) => {
      return state.notificationsBookingRequest;
    },

    /**
     * 顧客情報を返す（姓・名・メール・電話・住所など）
     *
     * @param {Object} state - Vuex state
     * @returns {Object}
     */
    customerInfo: (state: any) => {
      return state.customerInfo;
    },

    /**
     * 保護者情報を返す
     *
     * @param {Object} state - Vuex state
     * @returns {Object}
     */
    parentInfo: (state: any) => {
      return state.parentInfo;
    },

    /**
     * お子様情報を返す
     *
     * @param {Object} state - Vuex state
     * @returns {Object}
     */
    childInfo: (state: any) => {
      return state.childInfo;
    },

    /**
     * 子ども予約フラグを返す
     *
     * @param {Object} state - Vuex state
     * @returns {boolean}
     */
    isChildBooking: (state: any) => {
      return state.isChildBooking;
    },

    /**
     * 予約が送信完了済みかどうか
     *
     * @param {Object} state - Vuex state
     * @returns {boolean}
     */
    submited: (state: any) => {
      return state.submited;
    },

    /**
     * 合計金額を返す
     *
     * @param {Object} state - Vuex state
     * @returns {number}
     */
    totalPrice(state: any) {
      return state.totalPrice;
    },

    /**
     * 選択されたオプションだけの合計所要時間を返す
     *
     * @param {Object} state - 現在のVuex state
     * @returns {number} 分単位の合計
     */
    totalOptionsTime(state: any) {
      let totalTime = 0;

      state.selectedOptions.forEach(function (option: any) {
        totalTime += option.time;
      });

      return totalTime;
    },

    /**
     * メニュー＋オプションの合計所要時間を返す
     *
     * @param {Object} state - 現在のVuex state
     * @returns {number} 分単位の合計
     */
    totalTime(state: any) {
      let totalTime = state.selectedMenu.time;

      state.selectedOptions.forEach(function (option: any) {
        totalTime += option.time;
      });

      return totalTime;
    },

    /**
     * 選択されたスタッフ情報を返す
     *
     * @param {Object} state - Vuex state
     * @returns {Object}
     */
    selectedStaff: (state: any) => {
      return state.selectedStaff;
    },

    /**
     * 自動振り分け候補のスタッフID一覧を返す
     *
     * @param {Object} state - Vuex state
     * @returns {Array<number>}
     */
    autoStaffDistributionData: (state: any) => {
      return state.autoStaffDistribution.data;
    },

    /**
     * 実際に振り分けられたスタッフIDを返す
     *
     * @param {Object} state - Vuex state
     * @returns {number | string}
     */
    distributedStaffid: (state: any) => {
      return state.autoStaffDistribution.selectedStaffId;
    },

    /**
     * 悩みコンテンツリストを返す
     *
     * @param {Object} state - Vuex state
     * @returns {Array}
     */
    worryContents: (state: any) => {
      return state.worryContents;
    },
  },
};

/**
 * Booking Vuexモジュールをエクスポート
 * - 予約に関連する状態管理、アクション、ゲッター、ミューテーションを提供する
 * - 使用例：store.registerModule('booking', Booking)
 */
export default Booking;
